import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { RootState } from "~/app/store";
import type { CustomDispatch } from "~/app/store/types";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { ConfigurationNameDialog } from "~/domains/configurator/components/dialogs";
import { saveConfiguratorAction } from "~/features/configurator/Linear/redux/actions";
import { useTrackLCSaveConfigurationClick } from "~/features/configurator/Linear/hooks/useTrackLCClicks";
import { transformSavedConfig } from "~/features/configurator/Linear/savedConfigTransformer";

export const useRequestOfferPopup = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch() as CustomDispatch<RootState>;
  const navigate = useNavigate();
  const trackSave = useTrackLCSaveConfigurationClick();
  const { val: isModalVisible, setTrue: openModal, setFalse: closeModal } = useBooleanState(false);

  const submit = useCallback(
    async (name: string) => {
      const response = await dispatch(saveConfiguratorAction(name));
      if (!response) {
        throw new Error("Saving the configuration failed.");
      }
      trackSave();
      const items = transformSavedConfig([response]);
      items[0].isSelected = true;
      await navigate({
        to: "/store-finder",
        state: { shareLinearConfigurationData: items.map((item) => item.code) },
      });
    },
    [dispatch, navigate, trackSave],
  );

  return {
    openModal,
    component: isModalVisible ? (
      <ConfigurationNameDialog
        open
        title={t("page.linear.requestOffer.header")}
        label={t("page.linear.saveConfig.label")}
        requiredMessage={t("page.linear.saveConfig.helperLabel")}
        mandatoryText={t("editWishlistNamePopup.mandatory")}
        cancelLabel={t("cancel")}
        submitLabel={t("linearConfigSummary.card.requestOffer")}
        submitError={t("page.linear.saveConfig.result.failure")}
        onClose={closeModal}
        onSubmit={submit}
      />
    ) : null,
  };
};
